import { Alert, Button, Modal } from "react-bootstrap";
import type { IRegDataPreviewResult } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import type { ReconciliationGridCardModel } from "./reconciliationGridTypes";
import { buildPreviewModalSections } from "./previewModalRows";
import { RegDataPreviewFieldTable } from "./RegDataPreviewFieldTable";
import "./RegDataPreviewModal.css";

export interface RegDataPreviewModalProps {
    show: boolean;
    loading: boolean;
    preview: IRegDataPreviewResult | null;
    cardModels: ReconciliationGridCardModel[];
    revertDisabled?: boolean;
    onClose: () => void;
    onRePreview: () => void;
    onRevertStaged: (changeKey: string) => void;
}

export function RegDataPreviewModal({
    show,
    loading,
    preview,
    cardModels,
    revertDisabled = false,
    onClose,
    onRePreview,
    onRevertStaged,
}: RegDataPreviewModalProps) {
    const sections = buildPreviewModalSections(cardModels);
    const pendingCount = sections.reduce((sum, section) => sum + section.rows.length, 0);

    return (
        <Modal show={show} onHide={onClose} size="xl" centered scrollable className="reg-data-preview-modal">
            <Modal.Header closeButton>
                <Modal.Title>Review pending regulatory data changes</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && <div className="text-muted mb-3">Loading validation…</div>}

                {!loading && preview?.blockingErrors && preview.blockingErrors.length > 0 && (
                    <Alert variant="danger" className="py-2">
                        <strong>Blocking errors</strong>
                        {preview.blockingErrors.map((error, index) => (
                            <div key={index} className="small">
                                {error.message}
                            </div>
                        ))}
                    </Alert>
                )}

                {!loading && preview?.warnings && preview.warnings.length > 0 && (
                    <Alert variant="warning" className="py-2">
                        <strong>Warnings</strong>
                        {preview.warnings.map((warning, index) => (
                            <div key={index} className="small">
                                {warning.message}
                            </div>
                        ))}
                    </Alert>
                )}

                {!loading && preview?.usageWarnings && preview.usageWarnings.length > 0 && (
                    <Alert variant="info" className="py-2">
                        <strong>Usage warnings</strong>
                        {preview.usageWarnings.map((warning, index) => (
                            <div key={index} className="small">
                                {warning.message} ({warning.count})
                            </div>
                        ))}
                    </Alert>
                )}

                {pendingCount === 0 ? (
                    <p className="small text-muted mb-0">No pending changes.</p>
                ) : (
                    sections.map((section) => (
                        <div key={section.fieldKey} className="mb-4">
                            <h5 className="mb-2">{section.label}</h5>
                            <RegDataPreviewFieldTable
                                fieldKey={section.fieldKey}
                                rows={section.rows}
                                revertDisabled={revertDisabled}
                                onRevertStaged={onRevertStaged}
                            />
                        </div>
                    ))
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Close
                </Button>
                <Button variant="outline-primary" onClick={onRePreview} disabled={loading || pendingCount === 0}>
                    Re-run validation
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
